import { useQuery } from "@tanstack/react-query";
import React, { KeyboardEvent, useEffect, useMemo, useState } from "react";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import {
  NoticeProps,
  components,
  InputProps,
  Options,
  OptionsOrGroups,
  GroupBase,
  MultiValueGenericProps,
  OptionProps,
  StylesConfig
} from "react-select";
import { NotificationsLevel } from "../../lib/enums";
import api from "../../services/api";
import { show as showNotification } from "../../notifications/notifications";
import { pluralize } from "../../utils";
import { MenuItem, MenuItems } from "../@types";
import ModalWrapper from "../ModalWrapper";
import { tagsQueryKeys } from "./queryKeys";
import { createSelectTagOption, formatTagNotificationMessage } from "./utils";
import SelectWithPaging from "../form/select/SelectWithPaging";
import { Accessors } from "react-select/dist/declarations/src/useCreatable";
import TagsSelectionMessage from "./components/TagsSelectionMessage";
import RenderWhen from "../RenderWhen";

interface ITagsModal {
  show: boolean;
  handleShow: () => boolean;
  onFormSubmit: (selectedTags: MenuItems) => void;
  initialTags?: string[];
  selectedCountTotalOverLimit?: boolean;
  mixedSamplesInSelection?: boolean;
  noLocalSamplesFoundInSelection?: boolean;
}

const { Input, MultiValueContainer, NoOptionsMessage, Option } = components;

const CustomMultiValueContainer = (
  props: MultiValueGenericProps<MenuItem, true>
) => {
  return (
    <div
      className={"multiselect-value"}
      data-testid={`tags_modal_tag_${props.data.value}`}
      title={props.data.label}
    >
      <MultiValueContainer {...props}>{props.children}</MultiValueContainer>
    </div>
  );
};

const CustomOption = (props: OptionProps<MenuItem, true>) => {
  const { label, value } = props.data;
  return (
    <span data-testid={`tags_modal_option_${value || label}`}>
      <Option {...props}>{props.children}</Option>
    </span>
  );
};

const CustomNoOptionsMessage = (props: NoticeProps<MenuItem, true>) => {
  const { testIdKey } = props.selectProps ?? {};
  const selectedTags = props.getValue();
  const inputValue = props.selectProps.inputValue.trim();
  const isTagUnique =
    selectedTags.filter((item: MenuItem) => item.value === inputValue)
      .length === 0;
  const message = isTagUnique ? "No Options" : "Tag Already Exists";
  return (
    <div data-testid={`${testIdKey}_field_no_options`}>
      <NoOptionsMessage {...props}>{message}</NoOptionsMessage>
    </div>
  );
};

const CustomInput = (props: InputProps) => (
  <Input {...props} data-testid="tags_modal_input" />
);

const TagsModal = ({
  show,
  handleShow,
  onFormSubmit,
  initialTags,
  selectedCountTotalOverLimit = false,
  mixedSamplesInSelection = false,
  noLocalSamplesFoundInSelection = false
}: ITagsModal): JSX.Element => {
  const [inputValue, setInputValue] = useState<string>("");
  const [selectedTags, setSelectedTags] = useState<MenuItems>([]);

  useEffect(() => {
    if (initialTags) {
      const tagsToMenuItems = initialTags.map((tag: string) =>
        createSelectTagOption(tag)
      );
      setSelectedTags(tagsToMenuItems);
    }
  }, [initialTags]);

  const handleCreate = (value: string) => {
    // Handle multiple, comma separated tags typed in as a string and remove any duplicate or empty values from it
    if (value.indexOf(",") > -1) {
      const cleanedTagsList = value.split(",").map(tag => tag.trim());
      const nonDuplicateTags = cleanedTagsList
        .filter(
          (tag: string, index: number, self: string[]) =>
            self.indexOf(tag) === index && tag.trim() !== ""
        )
        .map((tag: string) => createSelectTagOption(tag));
      setSelectedTags(prev => [...prev, ...nonDuplicateTags]);
    } else {
      setSelectedTags(prev => [...prev, createSelectTagOption(inputValue)]);
    }
  };

  const areTagsInvalid = (tags: MenuItems) => {
    const tagRegex = /^[a-z0-9][a-z0-9 /._-]{1,41}$/i;
    const invalidTags = tags
      .filter(item => !tagRegex.test(item.value))
      .map(item => item.value);
    if (invalidTags?.length) {
      const message = formatTagNotificationMessage("Invalid tag", invalidTags);
      showNotification({
        level: NotificationsLevel.ERROR,
        message
      });
    }
    return !!invalidTags?.length;
  };

  const createLabel = (input: string) => {
    const inputTags = input.split(",")?.filter(item => item.trim().length);
    return (
      <b>
        {pluralize("Add Tag", inputTags.length)}: {input}
      </b>
    );
  };

  const handleOnChange = (value: MenuItems): void => {
    setSelectedTags(value);
  };

  const handleFormKeyDown = (event: KeyboardEvent<HTMLFormElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
    }
  };

  const { data: allTagsData } = useQuery({
    queryKey: tagsQueryKeys.getAllTags,
    queryFn: () => api.getAllTags(),
    select: ({ data }) => data.map((tag: string) => createSelectTagOption(tag))
  });

  const allTags = useMemo(() => allTagsData || [], [allTagsData]);

  const handleInputChange = (value: string) => setInputValue(value);

  const customFilterOption = (
    option: { label: string },
    inputValue: string
  ) => {
    const lowercaseInput = inputValue.toLowerCase().trim();
    const lowercasedOptionLabel = option.label.toLowerCase();
    return lowercasedOptionLabel.startsWith(lowercaseInput);
  };

  const filteredTags = useMemo(() => {
    if (!inputValue.trim()) {
      return allTags;
    }
    return allTags.filter((option: { label: string }) =>
      customFilterOption(option, inputValue)
    );
  }, [inputValue, allTags]);

  const isValidNewOption = (
    inputValue: string,
    value: Options<MenuItem>,
    options: OptionsOrGroups<MenuItem, GroupBase<MenuItem>>,
    accessors: Accessors<MenuItem>
  ): boolean => {
    if (inputValue == "") return false;
    const optionLabels = options.map((option: any) =>
      accessors.getOptionLabel(option)
    );
    const isMatch = optionLabels.some((label: string) => label === inputValue);
    return !isMatch;
  };

  const handleFormSubmit = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    if (areTagsInvalid(selectedTags)) {
      return;
    }

    onFormSubmit(selectedTags);
    handleShow();
  };

  const customSelectStyles: StylesConfig<
    MenuItem,
    true,
    GroupBase<MenuItem>
  > = {
    control: base => ({
      ...base,
      cursor: "pointer",
      color: "black"
    }),
    dropdownIndicator: base => ({
      ...base,
      color: "#343a40",
      ":hover": {
        color: "#343a40"
      }
    }),
    clearIndicator: base => ({
      ...base,
      cursor: "pointer",
      ":hover": {
        color: "#343a40"
      },
      color: "#343a40"
    })
  };

  return (
    <ModalWrapper
      data-testid="tags_modal"
      show={show}
      title="Edit Tags"
      onHide={handleShow}
      className="modal--borderless modal--with-overflow modal--footer-left"
      backdrop="static"
      size={selectedTags.length > 30 ? "lg" : undefined}
      footer={
        <div className="pull-right">
          <Button
            variant="prompt-secondary"
            onClick={handleShow}
            data-testid="tags_cancel_button"
            className="me-5"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="prompt-primary"
            onClick={handleFormSubmit}
            data-testid="tags_submit_button"
            disabled={noLocalSamplesFoundInSelection}
          >
            Save
          </Button>
        </div>
      }
      centered
    >
      <>
        <RenderWhen
          condition={
            noLocalSamplesFoundInSelection ||
            mixedSamplesInSelection ||
            selectedCountTotalOverLimit
          }
        >
          <TagsSelectionMessage
            hasNoLocalSamples={noLocalSamplesFoundInSelection}
            hasMixedSamples={mixedSamplesInSelection}
            selectedCountTotalOverLimit={selectedCountTotalOverLimit}
          />
        </RenderWhen>
        <Form onKeyDown={handleFormKeyDown}>
          <SelectWithPaging
            isMulti
            autoFocus
            menuOptions={filteredTags}
            value={selectedTags}
            inputValue={inputValue}
            isValidNewOption={isValidNewOption}
            closeMenuOnSelect
            formatCreateLabel={createLabel}
            createOptionPosition="first"
            placeholder={noLocalSamplesFoundInSelection ? "" : "Add Tags..."}
            maxMenuHeight={200}
            menuShouldScrollIntoView={false}
            onChange={handleOnChange}
            onInputChange={handleInputChange}
            onCreateOption={handleCreate}
            openMenuOnClick={false}
            styles={customSelectStyles}
            components={{
              Input: CustomInput,
              Option: CustomOption,
              MultiValueContainer: CustomMultiValueContainer,
              NoOptionsMessage: CustomNoOptionsMessage
            }}
            isDisabled={noLocalSamplesFoundInSelection}
          />
        </Form>
      </>
    </ModalWrapper>
  );
};

export default TagsModal;
